import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

export default function FeaturedTours() {
  const tours = [
    {
      id: 1,
      title: 'European Grand Tour',
      category: 'Cultural',
      image: 'https://public.readdy.ai/ai/img_res/cea0ca770557ead2843aa3e1fac2b4bf.jpg',
      duration: '14 Days',
      groupSize: '12-16 People',
      price: 2899,
      description: 'Explore the best of Europe with visits to Paris, Rome, Barcelona, and more',
    },
    {
      id: 2,
      title: 'Tropical Paradise Escape',
      category: 'Beach',
      image: 'https://readdy.ai/api/search-image?query=breathtaking%20tropical%20island%20paradise%20with%20pristine%20turquoise%20lagoon%20surrounded%20by%20lush%20green%20palm%20trees%20and%20powdery%20white%20sand%20beach%20under%20brilliant%20blue%20sky%20creating%20idyllic%20vacation%20destination%20travel%20photography&width=800&height=600&seq=tour2&orientation=landscape',
      duration: '7 Days',
      groupSize: '8-12 People',
      price: 1599,
      description: 'Relax on pristine beaches in the Maldives and enjoy luxury resort living',
    },
    {
      id: 3,
      title: 'Asian Adventure',
      category: 'Adventure',
      image: 'https://readdy.ai/api/search-image?query=stunning%20asian%20temple%20complex%20with%20ornate%20golden%20architecture%20surrounded%20by%20vibrant%20tropical%20gardens%20and%20misty%20mountains%20in%20background%20creating%20mystical%20spiritual%20atmosphere%20perfect%20for%20adventure%20travel%20photography&width=800&height=600&seq=tour3&orientation=landscape',
      duration: '10 Days',
      groupSize: '10-14 People',
      price: 2199,
      description: 'Discover the wonders of Thailand, Vietnam, and Cambodia',
    },
    {
      id: 4,
      title: 'African Safari Experience',
      category: 'Wildlife',
      image: 'https://readdy.ai/api/search-image?query=majestic%20african%20savanna%20landscape%20with%20golden%20grasslands%20acacia%20trees%20and%20dramatic%20sunset%20sky%20creating%20stunning%20wildlife%20safari%20atmosphere%20perfect%20for%20adventure%20travel%20photography&width=600&height=900&seq=tour4&orientation=portrait',
      duration: '12 Days',
      groupSize: '8-10 People',
      price: 3499,
      description: 'Witness incredible wildlife in Kenya and Tanzania national parks',
    },
    {
      id: 5,
      title: 'South American Discovery',
      category: 'Cultural',
      image: 'https://readdy.ai/api/search-image?query=spectacular%20south%20american%20mountain%20landscape%20with%20ancient%20ruins%20colorful%20traditional%20architecture%20and%20dramatic%20peaks%20creating%20vibrant%20cultural%20travel%20destination%20photography&width=800&height=600&seq=tour5&orientation=landscape',
      duration: '15 Days',
      groupSize: '12-16 People',
      price: 3299,
      description: 'Journey through Peru, Bolivia, and Argentina exploring ancient cultures',
    },
    {
      id: 6,
      title: 'Australian Outback',
      category: 'Adventure',
      image: 'https://readdy.ai/api/search-image?query=stunning%20australian%20outback%20landscape%20with%20red%20desert%20rock%20formations%20under%20brilliant%20blue%20sky%20creating%20dramatic%20natural%20adventure%20travel%20photography&width=800&height=600&seq=tour6&orientation=landscape',
      duration: '9 Days',
      groupSize: '10-12 People',
      price: 2799,
      description: 'Experience the rugged beauty of Australia\'s interior and coastal wonders',
    },
  ];

  return (
    <section className="py-24 bg-[#FAF8F5]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="mb-16">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-2 h-2 rounded-full bg-accent-orange"></div>
            <span className="text-xs uppercase tracking-wider text-accent-orange font-semibold">
              Popular Packages
            </span>
          </div>
          <h2 className="text-5xl font-bold text-navy-900 mb-6 leading-tight">
            Your Next Adventure<br />Awaits
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl">
            Handpicked experiences designed for every type of traveler
          </p>
        </div>

        {/* Tours Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {tours.map((tour, index) => (
            <motion.div
              key={tour.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="bg-white rounded-3xl shadow-lg hover:shadow-2xl transition-all group cursor-pointer"
              data-product-shop
            >
              {/* Image */}
              <div className="relative h-72 overflow-hidden rounded-t-3xl">
                <img
                  src={tour.image}
                  alt={tour.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                
                {/* Category Badge */}
                <div className="absolute top-4 left-4">
                  <span className="px-4 py-1.5 bg-accent-pink text-white text-xs font-semibold rounded-full">
                    {tour.category}
                  </span>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <h3 className="text-2xl font-bold text-navy-900 mb-3 leading-tight">
                  {tour.title}
                </h3>
                
                <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                  {tour.description}
                </p>

                {/* Details */}
                <div className="flex items-center gap-4 mb-4 text-sm text-gray-500">
                  <div className="flex items-center gap-1.5">
                    <i className="ri-time-line"></i>
                    <span>{tour.duration}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <i className="ri-group-line"></i>
                    <span>{tour.groupSize}</span>
                  </div>
                </div>

                {/* Price & CTA */}
                <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">From</p>
                    <p className="text-3xl font-bold bg-gradient-to-r from-primary-600 to-accent-pink bg-clip-text text-transparent">
                      ${tour.price.toLocaleString()}
                    </p>
                  </div>
                  <Link
                    to="/tours"
                    className="flex items-center gap-2 text-accent-pink font-semibold hover:gap-3 transition-all whitespace-nowrap"
                  >
                    View Details
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* View All Button */}
        <div className="text-center mt-12">
          <Link
            to="/tours"
            className="inline-flex items-center gap-2 px-8 py-4 bg-navy-900 text-white rounded-full font-semibold hover:bg-navy-800 transition-all cursor-pointer whitespace-nowrap"
          >
            View All Tours
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </div>
    </section>
  );
}